/**
 * useAuth Hook
 * Custom hook for authentication state and actions
 */

import { useSelector, useDispatch } from 'react-redux'
import { tokenManager } from '@/utils/tokenManager'
import { RootState } from '@/app/store'
import {
  setCredentials,
  setUser,
  setContexts,
  setCurrentContext,
  setContextToken,
  setContextData,
  setActiveWorkspace,
  setLoading,
  setError,
  resetAuthState,
} from '@/store/slices/auth.slice'
import { resetPermissions } from '@/store/slices/permissions.slice'
import { resetNavigation } from '@/store/slices/navigation.slice'
import { clearPermissionCatalogCache } from '@/config/rbac/permissionCatalogCache'
import { User, UserContext, ContextData } from '@/types/shared/auth.types'
import { clearTenantStorage, getTenantFromHostname } from '@/utils/tenant.util'
import { navigationService } from '@/services/api/shared/navigation.service'
import { authService } from '@/services/api/shared/auth.service'
import { socketService } from '@/services/realtime/socket.service'
import { useUIPreferences } from '@/contexts/UIPreferencesContext'

export function useAuth() {
  const dispatch = useDispatch()
  const auth = useSelector((state: RootState) => state.auth)
  const isRBACReady = useSelector((state: RootState) => state.permissions.isRBACReady === true)
  const { setUIPreferences } = useUIPreferences()

  return {
    // State
    user: auth.user,
    contexts: auth.contexts,
    currentContext: auth.currentContext,
    contextData: auth.contextData, // Full context data from cache
    activeWorkspace: auth.activeWorkspace,
    accessToken: auth.accessToken,
    refreshToken: auth.refreshToken,
    contextToken: auth.contextToken,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    isRBACReady,

    // Actions
    setCredentials: (payload: {
      user: User
      accessToken: string
      refreshToken: string
      contextToken?: string
      contexts?: UserContext[]
    }) => dispatch(setCredentials(payload)),
    setUser: (user: User) => dispatch(setUser(user)),
    setContexts: (contexts: UserContext[]) => dispatch(setContexts(contexts)),
    setCurrentContext: (context: UserContext) => dispatch(setCurrentContext(context)),
    setContextToken: (token: string) => dispatch(setContextToken(token)),
    setContextData: (data: ContextData | null) => dispatch(setContextData(data)),
    setActiveWorkspace: (w: string | null) => dispatch(setActiveWorkspace(w)),
    setLoading: (loading: boolean) => dispatch(setLoading(loading)),
    setError: (error: string | null) => dispatch(setError(error)),
    logout: async () => {
      try {
        socketService.disconnect()
        // Invalidate UI preferences (context + storage via clearAuth)
        setUIPreferences(null)
        // First, call the backend logout API to clear httpOnly cookies
        // This must be done before clearing local state
        await authService.logout()
      } catch (error) {
        // Log error but continue with local cleanup
      } finally {
        // Clear Redux state
        dispatch(resetAuthState())
        dispatch(resetPermissions())
        dispatch(resetNavigation())
        clearPermissionCatalogCache()

        // Clear all auth data using tokenManager (tenant-aware)
        tokenManager.clearAllAuthData()
        
        // Clear tenant-specific storage
        const tenantId = getTenantFromHostname()
        clearTenantStorage(tenantId)
        
        // Clear sessionStorage
        try {
          sessionStorage.clear()
        } catch (error) {
        }
      }
    },
    refreshAuth: async () => {
      try {
        // Fetch updated user context to refresh organization/context list
        const userContextResponse = await navigationService.getUserContext()
        const accessibleDomains = userContextResponse?.domainNavigation?.accessibleDomains || []
        const transformedContexts = navigationService.transformDomainsToContexts(accessibleDomains)
        dispatch(setContexts(transformedContexts))
        return transformedContexts
      } catch (error) {
        throw error
      }
    },
  }
}

